import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Loader2,
  Phone,
  Mail,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const STATUS_META = {
  paid: { en: "Paid", ar: "مدفوع", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  pending_payment: { en: "Pending payment", ar: "بانتظار الدفع", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  new: { en: "New", ar: "جديد", color: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  refunded: { en: "Refunded", ar: "مُسترد", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  cancelled: { en: "Cancelled", ar: "ملغي", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

function StatusPill({ status, lang }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] uppercase tracking-[0.18em] border ${meta.color}`}
    >
      <Icon className="w-3 h-3" />
      {meta[lang] || meta.en}
    </span>
  );
}

function formatDate(iso, isAr) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? "ar-KW" : "en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminBookings() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);

  const t = useMemo(
    () => ({
      back: isAr ? "← لوحة التحكم" : "← Dashboard",
      eyebrow: isAr ? "أرشيف الحجوزات" : "Bookings archive",
      title: isAr ? "سجل الحجوزات السابقة." : "Past consultation bookings.",
      lead: isAr
        ? "سجل الحجوزات التي تمت قبل إلغاء نظام الحجز عبر الموقع. يمكنك مراجعة التفاصيل، التواصل مع العميل، أو استرداد المبلغ من الحجوزات المدفوعة."
        : "Archive of bookings created before the website booking system was disabled. You can review details, contact the client, or refund paid bookings.",
      filters: {
        all: isAr ? "الكل" : "All",
        paid: isAr ? "مدفوع" : "Paid",
        pending_payment: isAr ? "بانتظار الدفع" : "Pending",
        new: isAr ? "جديد" : "New",
        refunded: isAr ? "مُسترد" : "Refunded",
      },
      search: isAr ? "ابحث بالاسم أو البريد…" : "Search by name or email…",
      empty: isAr ? "لا توجد حجوزات." : "No bookings yet.",
      cols: {
        client: isAr ? "العميل" : "Client",
        practice: isAr ? "المجال" : "Practice",
        slot: isAr ? "الموعد" : "Slot",
        status: isAr ? "الحالة" : "Status",
        received: isAr ? "تاريخ الاستلام" : "Received",
      },
      details: isAr ? "تفاصيل الحجز" : "Booking details",
      practiceArea: isAr ? "مجال القانون" : "Practice area",
      preferredDate: isAr ? "التاريخ المفضل" : "Preferred date",
      preferredTime: isAr ? "الوقت المفضل" : "Preferred time",
      phone: isAr ? "الهاتف" : "Phone",
      email: isAr ? "البريد" : "Email",
      notes: isAr ? "الملاحظات" : "Notes",
      meet: isAr ? "رابط Google Meet" : "Google Meet link",
      paymentId: isAr ? "معرف الدفع" : "Payment ID",
      created: isAr ? "أُنشئ في" : "Created",
      refund: isAr ? "استرداد المبلغ" : "Issue refund",
      refundConfirm: isAr
        ? "هل أنت متأكد من استرداد هذا المبلغ؟ لا يمكن التراجع."
        : "Refund this booking? This cannot be undone.",
      refundOk: isAr ? "تم الاسترداد." : "Refund issued.",
      refundErr: isAr ? "تعذّر الاسترداد." : "Refund failed.",
      close: isAr ? "إغلاق" : "Close",
      whatsapp: isAr ? "واتساب" : "WhatsApp",
      callClient: isAr ? "اتصل" : "Call",
      emailClient: isAr ? "أرسل بريد" : "Email",
    }),
    [isAr]
  );

  const load = useCallback(() => {
    api
      .get("/admin/bookings")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/bookings" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const filtered = (items || []).filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (query) {
      const q = query.toLowerCase();
      return (
        (b.name || "").toLowerCase().includes(q) ||
        (b.email || "").toLowerCase().includes(q) ||
        (b.phone || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const doRefund = async (booking) => {
    if (!window.confirm(t.refundConfirm)) return;
    setRefundBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/refund`, {});
      toast.success(t.refundOk);
      setSelected(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.refundErr);
    } finally {
      setRefundBusy(false);
    }
  };

  const waLink = (phone) => {
    const clean = (phone || "").replace(/[^0-9]/g, "");
    return clean ? `https://wa.me/${clean}` : null;
  };

  return (
    <div data-testid="admin-bookings-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 dark:hover:text-amber-500 transition-colors mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t.back}
      </Link>

      <div className="grid lg:grid-cols-12 gap-12 mb-8">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            {t.lead}
          </p>
        </div>
      </div>

      {/* Booking system archived banner */}
      <div
        data-testid="bookings-archive-banner"
        className="mb-8 border-s-4 ps-4 py-3 bg-amber-50/60"
        style={{ borderColor: "var(--gold-deep)" }}
      >
        <p className="text-sm font-medium" style={{ color: "var(--gold-deep)" }}>
          نظام الحجز عبر الموقع مُلغى — للاطلاع فقط
        </p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          الحجوزات الجديدة تصل عبر واتساب مباشرةً. السجل أدناه يعرض الحجوزات
          السابقة فقط للأرشيف (يمكن مراجعتها وإجراء استرداد للمدفوعات القديمة عند
          الحاجة).
        </p>
      </div>

      {/* CONTROLS */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {Object.entries(t.filters).map(([k, label]) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 h-9 text-xs uppercase tracking-[0.18em] border transition-colors ${
              filter === k
                ? "bg-slate-900 dark:bg-amber-500 text-white dark:text-slate-900 border-slate-900 dark:border-amber-500"
                : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-900 dark:hover:border-amber-500"
            }`}
          >
            {label}
          </button>
        ))}
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.search}
          data-testid="bookings-search"
          dir="auto"
          className="ms-auto h-9 px-3 rounded-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm w-full max-w-xs"
        />
      </div>

      {/* TABLE */}
      <div className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
        {items === null ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">{t.empty}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.client}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.practice}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.slot}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.status}</th>
                <th className="text-start px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 font-normal">{t.cols.received}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`booking-row-${b.id}`}
                  onClick={() => setSelected(b)}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                >
                  <td className="px-5 py-4">
                    <p className="font-medium">{b.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{b.email}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                    {b.practice_area || "—"}
                  </td>
                  <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      {b.preferred_date}
                      {b.preferred_time ? ` · ${b.preferred_time}` : ""}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <StatusPill status={b.status} lang={lang} />
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-500">
                    {formatDate(b.created_at, isAr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* DETAILS DRAWER */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-stretch justify-end"
          onClick={() => setSelected(null)}
          data-testid="booking-details-overlay"
        >
          <div
            className="w-full max-w-xl bg-white dark:bg-slate-900 h-full overflow-y-auto border-s border-slate-200 dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-details-panel"
          >
            <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <p className="eyebrow">{t.details}</p>
              <button
                onClick={() => setSelected(null)}
                data-testid="close-details"
                className="text-slate-400 hover:text-slate-900 dark:hover:text-amber-500 text-sm uppercase tracking-[0.18em]"
              >
                {t.close}
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <h2 className="font-display text-3xl">{selected.name}</h2>
                <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
                  <StatusPill status={selected.status} lang={lang} />
                  <span className="text-slate-500">
                    {formatDate(selected.created_at, isAr)}
                  </span>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex flex-wrap gap-2">
                <a
                  href={`mailto:${selected.email}`}
                  data-testid="action-email"
                  className="inline-flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.18em] border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-amber-500 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" /> {t.emailClient}
                </a>
                {selected.phone ? (
                  <a
                    href={`tel:${selected.phone}`}
                    data-testid="action-call"
                    className="inline-flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.18em] border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-amber-500 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" /> {t.callClient}
                  </a>
                ) : null}
                {waLink(selected.phone) ? (
                  <a
                    href={waLink(selected.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="action-whatsapp"
                    className="inline-flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.18em] transition-colors"
                    style={{ borderWidth: 1, borderStyle: "solid", borderColor: "var(--wa)", color: "var(--wa)" }}
                  >
                    <Phone className="w-3.5 h-3.5" /> {t.whatsapp}
                  </a>
                ) : null}
              </div>

              {/* Details rows */}
              <dl className="divide-y divide-slate-100 dark:divide-slate-800 border-y border-slate-200 dark:border-slate-800">
                <Row label={t.email} value={selected.email} />
                <Row label={t.phone} value={selected.phone || "—"} />
                <Row label={t.practiceArea} value={selected.practice_area || "—"} />
                <Row label={t.preferredDate} value={selected.preferred_date || "—"} />
                <Row label={t.preferredTime} value={selected.preferred_time || "—"} />
                <Row
                  label={t.notes}
                  value={
                    selected.notes ? (
                      <span className="whitespace-pre-wrap">{selected.notes}</span>
                    ) : (
                      "—"
                    )
                  }
                />
                {selected.meet_link ? (
                  <Row
                    label={t.meet}
                    value={
                      <a
                        href={selected.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sky-700 hover:underline"
                      >
                        Meet <ExternalLink className="w-3 h-3" />
                      </a>
                    }
                  />
                ) : null}
                {selected.payment_id ? (
                  <Row label={t.paymentId} value={<code className="text-xs">{selected.payment_id}</code>} />
                ) : null}
                <Row label={t.created} value={formatDate(selected.created_at, isAr)} />
              </dl>

              {/* Refund */}
              {selected.status === "paid" && (
                <Button
                  type="button"
                  disabled={refundBusy}
                  onClick={() => doRefund(selected)}
                  data-testid="refund-booking"
                  variant="outline"
                  className="rounded-sm h-11 w-full border-red-200 text-red-700 hover:bg-red-50"
                >
                  {refundBusy ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <XCircle className="w-4 h-4 me-2" />}
                  {t.refund}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-3">
      <dt className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</dt>
      <dd className="col-span-2 text-sm text-slate-900 dark:text-slate-100 break-words">{value}</dd>
    </div>
  );
}
